import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import secureApiClient from '../utils/secureApiClient';
import { ROLE_DISPLAY_NAMES } from '../config/roles';

const HIPAA_AGREEMENT_TEXT = `HIPAA WORKFORCE MEMBER CONFIDENTIALITY & PRIVACY AGREEMENT

Integrity Mental Health & Coaching

As a member of the workforce at Integrity Mental Health & Coaching (the "Practice"), you will have access to Protected Health Information ("PHI") and Electronic Protected Health Information ("ePHI") as defined under the Health Insurance Portability and Accountability Act of 1996 (HIPAA) and the Health Information Technology for Economic and Clinical Health (HITECH) Act. By accepting this agreement, you acknowledge and agree to the following:

1. CONFIDENTIALITY OF PROTECTED HEALTH INFORMATION

You agree to maintain the confidentiality of all PHI and ePHI you access, use, or disclose in the course of your work. This includes, but is not limited to, client names, dates of service, diagnoses, treatment records, billing information, and any other information that could identify a client or be used to identify a client in combination with other data.

You will only access, use, or disclose PHI to the extent necessary to perform your authorized job duties ("minimum necessary" standard). You will not access client records for personal curiosity or for any purpose unrelated to your assigned responsibilities.

2. AUTHORIZED USE AND DISCLOSURE

You may use or disclose PHI only for purposes of treatment, payment, or healthcare operations as permitted under HIPAA, or as expressly authorized in writing by the client. Any other use or disclosure requires prior written authorization from the Privacy Officer or Practice Administrator.

3. SECURITY OBLIGATIONS

You agree to:
  a) Use only authorized devices and systems to access PHI and ePHI.
  b) Keep your login credentials (username and password) confidential and not share them with anyone.
  c) Log out of all systems when your session is complete.
  d) Lock or secure your screen when leaving a workstation unattended.
  e) Not transmit PHI via unencrypted email, text message, or other unsecured channels unless an appropriate Business Associate Agreement is in place.
  f) Report any lost or stolen devices that may contain PHI immediately to the Privacy Officer.

4. BREACH REPORTING

You are required to report any actual or suspected unauthorized access, use, or disclosure of PHI to the Practice's Privacy Officer immediately upon discovery — even if you are uncertain whether a breach has occurred. Timely reporting is critical to fulfilling the Practice's obligations under HIPAA and HITECH. Failure to report a known or suspected breach is itself a violation of this agreement.

5. WORKFORCE TRAINING

You acknowledge that you have received or will receive training on the Practice's HIPAA Privacy and Security policies and procedures. You agree to complete all required training as assigned and to comply with the Practice's policies at all times.

6. CLIENT RIGHTS

You agree to support and uphold the rights of clients under HIPAA, including the right to access their records, request amendments, receive an accounting of disclosures, and request restrictions on uses and disclosures of their PHI.

7. CONSEQUENCES OF VIOLATIONS

Unauthorized access, use, or disclosure of PHI is a serious matter. Violations of HIPAA and this agreement may result in:
  a) Disciplinary action up to and including termination of employment or contractor relationship.
  b) Civil monetary penalties ranging from $100 to $50,000 per violation, up to $1.9 million per calendar year for identical violations.
  c) Criminal penalties including fines up to $250,000 and imprisonment up to 10 years for willful misuse of PHI.
  d) Referral to the U.S. Department of Health and Human Services Office for Civil Rights (OCR).

8. DURATION

This agreement applies for the entire duration of your association with the Practice and survives termination. Your obligations regarding PHI you accessed during your tenure remain in effect indefinitely after separation.

9. ACKNOWLEDGEMENT

By checking the box below and creating your account, you certify that:
  • You have read and understand this agreement in its entirety.
  • You agree to comply with all terms stated above.
  • You understand that violations may result in disciplinary action and/or civil or criminal liability.
  • You consent to this acknowledgement being recorded with a timestamp in the Practice's records.`;

const StaffSignUp = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { signup } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [tokenStatus, setTokenStatus] = useState('checking');
  const [step, setStep] = useState(1); // 1 = password setup, 2 = HIPAA agreement
  const [formData, setFormData] = useState({ firstName: '', lastName: '', password: '', confirmPassword: '' });
  const [hipaaChecked, setHipaaChecked] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Validate token on load
  useEffect(() => {
    if (!token) { setTokenStatus('invalid'); return; }

    fetch(secureApiClient.baseURLs.cloudFunctions.validateStaffInvitation, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    })
      .then(async response => {
        if (!response.ok) throw new Error('Invalid invitation');
        const data = await response.json();
        setInvitation(data);
        const parts = (data.name || '').split(' ');
        setFormData(prev => ({
          ...prev,
          firstName: parts[0] || '',
          lastName: parts.slice(1).join(' ') || '',
        }));
        setTokenStatus('valid');
      })
      .catch(() => setTokenStatus('invalid'));
  }, [token]);

  const handle = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStep1Submit = e => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) return setError('Passwords do not match.');
    if (formData.password.length < 6) return setError('Password must be at least 6 characters.');
    setError('');
    setStep(2);
    // Scroll to top of the card
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStep2Submit = async e => {
    e.preventDefault();
    if (!hipaaChecked) return;

    try {
      setError('');
      setLoading(true);
      await signup({
        email: invitation.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        invitationToken: token,
        hipaaAcknowledgedAt: new Date().toISOString(),
      });
      navigate('/verify-email');
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:border-transparent';

  // ── Token checking ──────────────────────────────────────────
  if (tokenStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f0f4f6' }}>
        <p className="text-gray-500 text-sm">Validating your invitation…</p>
      </div>
    );
  }

  // ── Invalid / expired / used states ────────────────────────
  if (tokenStatus !== 'valid') {
    const messages = {
      invalid: 'This invitation link is invalid or does not exist.',
      expired: 'This invitation link has expired. Please ask your admin to send a new one.',
      used:    'This invitation link has already been used.',
    };
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f0f4f6' }}>
        <div className="bg-white rounded-2xl shadow-md w-full max-w-md p-8 text-center">
          <div className="text-4xl mb-4">🔒</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Invalid Invitation</h1>
          <p className="text-sm text-gray-500 mb-6">{messages[tokenStatus]}</p>
          <Link to="/login" className="text-sm font-medium hover:underline" style={{ color: '#1e562d' }}>
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  // ── Valid token — show signup form ──────────────────────────
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f0f4f6' }}>

      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-5" style={{ backgroundColor: '#1e562d' }}>
        <span className="text-white font-bold text-lg tracking-wide">
          Integrity Mental Health &amp; Coaching
        </span>
        <Link to="/login"
          className="text-sm font-medium px-4 py-2 rounded-lg border border-white text-white hover:bg-white hover:text-teal-800 transition-colors">
          Sign In
        </Link>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-2xl shadow-md w-full max-w-lg p-8">

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                style={{ backgroundColor: '#1e562d' }}>
                {step > 1 ? '✓' : '1'}
              </div>
              <span className="text-xs font-medium" style={{ color: step === 1 ? '#1e562d' : '#6b7280' }}>
                Account Setup
              </span>
            </div>
            <div className="h-px w-8 bg-gray-300" />
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                style={{
                  backgroundColor: step === 2 ? '#1e562d' : '#e5e7eb',
                  color: step === 2 ? 'white' : '#9ca3af'
                }}>
                2
              </div>
              <span className="text-xs font-medium" style={{ color: step === 2 ? '#1e562d' : '#9ca3af' }}>
                HIPAA Agreement
              </span>
            </div>
          </div>

          {/* ── STEP 1: Password setup ── */}
          {step === 1 && (
            <>
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold mb-1" style={{ color: '#1e562d' }}>
                  Staff Account Setup
                </h1>
                <p className="text-sm text-gray-500">
                  You've been invited as a{' '}
                  <span className="font-semibold" style={{ color: '#1e562d' }}>
                    {ROLE_DISPLAY_NAMES[invitation.role] || invitation.role}
                  </span>
                </p>
              </div>

              <div className="mb-5 p-3 rounded-lg bg-brand-50 border border-brand-100 text-sm text-brand-800">
                <span className="font-medium">Signing up as:</span> {invitation.email}
              </div>

              {error && (
                <div className="mb-5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleStep1Submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input name="firstName" type="text" required
                      value={formData.firstName} onChange={handle}
                      className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input name="lastName" type="text" required
                      value={formData.lastName} onChange={handle}
                      className={inputClass} />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input name="password" type="password" required
                    placeholder="At least 6 characters"
                    value={formData.password} onChange={handle}
                    className={inputClass} />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <input name="confirmPassword" type="password" required
                    placeholder="Repeat your password"
                    value={formData.confirmPassword} onChange={handle}
                    className={inputClass} />
                </div>

                <button type="submit"
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm mt-2 hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#1e562d' }}>
                  Next: Review HIPAA Agreement →
                </button>
              </form>

              <p className="text-xs text-gray-400 text-center mt-6 leading-relaxed">
                This invitation is valid for 48 hours and can only be used once.
              </p>
            </>
          )}

          {/* ── STEP 2: HIPAA agreement ── */}
          {step === 2 && (
            <>
              <div className="text-center mb-5">
                <h1 className="text-2xl font-bold mb-1" style={{ color: '#1e562d' }}>
                  HIPAA Workforce Agreement
                </h1>
                <p className="text-sm text-gray-500">
                  Please read and acknowledge before creating your account.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Scrollable agreement text */}
              <div
                className="border border-gray-200 rounded-xl p-4 mb-5 overflow-y-auto text-xs text-gray-600 leading-relaxed whitespace-pre-wrap font-mono bg-gray-50"
                style={{ maxHeight: '280px' }}>
                {HIPAA_AGREEMENT_TEXT}
              </div>

              <form onSubmit={handleStep2Submit}>
                <div className="flex items-start gap-3 mb-5">
                  <input
                    id="hipaa"
                    type="checkbox"
                    checked={hipaaChecked}
                    onChange={e => setHipaaChecked(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0"
                    style={{ accentColor: '#1e562d' }}
                  />
                  <label htmlFor="hipaa" className="text-sm text-gray-700 leading-snug cursor-pointer">
                    I have read, understand, and agree to the HIPAA Workforce Member
                    Confidentiality & Privacy Agreement above. I understand that my
                    acknowledgement will be recorded with a timestamp.
                  </label>
                </div>

                <button type="submit" disabled={loading || !hipaaChecked}
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: '#1e562d' }}>
                  {loading ? 'Creating your account…' : 'I Agree & Create Account →'}
                </button>
              </form>

              <button
                onClick={() => { setStep(1); setError(''); }}
                className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                ← Back
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffSignUp;
